﻿/**
 * The media contract of 搂9.3, in one component.
 *
 * - Toggle OFF (default): a placeholder block naming the media type, with a per-block
 *   "Load media" action that simply mounts `<img src="/api/media/:assetId">`.
 * - Toggle ON: the element mounts lazily, when the block scrolls into view (IntersectionObserver).
 * - A `failed` asset (server-side `cache_status`) or a failed element load renders the placeholder
 *   plus a retry that re-requests with `?refresh=1`.
 * - Every block sits inside an `ErrorBoundary`, so a broken asset can never take down its card,
 *   its table row or the Profile render.
 *
 * The client only ever holds an asset id 鈥?the proxy never accepts a URL (搂9.1).
 */

import { useCallback, useState } from 'react';
import type { MediaRefDTO, ProfileKind } from '@lw/shared';
import { mediaUrl } from '../api/media';
import { useInView } from '../lib/hooks';
import { MEDIA_KIND_LABEL } from '../lib/labels';
import { ErrorBoundary } from './ErrorBoundary';
import { cx } from './cx';

export type MediaBlockProps = {
  media: MediaRefDTO;
  /** `settings.mediaEnabled` 鈥?decides lazy auto-load vs. explicit "Load media" (搂9.3). */
  mediaEnabled: boolean;
  alt?: string;
  className?: string;
  /** Fixed aspect ratio hint for the placeholder, e.g. `16 / 9`. */
  ratio?: string;
};

type LoadState = 'idle' | 'loading' | 'loaded' | 'failed';

function Placeholder({
  media,
  label,
  action,
  actionLabel,
  ratio,
  busy,
}: {
  media: MediaRefDTO;
  label: string;
  action: () => void;
  actionLabel: string;
  ratio?: string;
  busy?: boolean;
}) {
  return (
    <div className="media__placeholder" style={ratio ? { aspectRatio: ratio } : undefined}>
      <span className="media__kind">{MEDIA_KIND_LABEL[media.kind]}</span>
      <span className="media__hint">{label}</span>
      <button type="button" className="table-action" onClick={action} disabled={busy}>
        {actionLabel}
      </button>
    </div>
  );
}

function MediaElement({ media, mediaEnabled, alt, className, ratio }: MediaBlockProps) {
  // A server-side `failed` asset starts in the failed state: only an explicit retry (?refresh=1)
  // may re-attempt it, never an automatic load.
  const [state, setState] = useState<LoadState>(media.status === 'failed' ? 'failed' : 'idle');
  const [refresh, setRefresh] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const start = useCallback(() => {
    setState((current) => (current === 'idle' ? 'loading' : current));
  }, []);

  const retry = useCallback(() => {
    setRefresh(true);
    setAttempt((n) => n + 1);
    setState('loading');
  }, []);

  const autoLoad = mediaEnabled && state === 'idle';
  const ref = useInView<HTMLDivElement>(autoLoad, start, { rootMargin: '300px' });

  const src = `${mediaUrl(media.assetId, { refresh: refresh || undefined })}${
    refresh ? `&a=${attempt}` : ''
  }`;
  const showElement = state === 'loading' || state === 'loaded';

  return (
    <div
      className={cx('media', `media--${media.kind}`, className)}
      ref={ref}
      data-status={state}
    >
      {state === 'failed' ? (
        <Placeholder
          media={media}
          label="This media could not be loaded."
          action={retry}
          actionLabel="Retry"
          ratio={ratio}
        />
      ) : null}

      {state === 'idle' && !mediaEnabled ? (
        <Placeholder
          media={media}
          label="Media loading is off in Settings."
          action={start}
          actionLabel="Load media"
          ratio={ratio}
        />
      ) : null}

      {state === 'idle' && mediaEnabled ? (
        <div className="media__placeholder" style={ratio ? { aspectRatio: ratio } : undefined}>
          <span className="media__kind">{MEDIA_KIND_LABEL[media.kind]}</span>
        </div>
      ) : null}

      {showElement && media.kind === 'video' ? (
        <video
          className="media__el"
          src={src}
          controls
          preload="metadata"
          onLoadedMetadata={() => setState('loaded')}
          onError={() => setState('failed')}
        />
      ) : null}

      {showElement && media.kind === 'document' ? (
        <a className="media__doc" href={src} target="_blank" rel="noreferrer noopener">
          Open document
        </a>
      ) : null}

      {showElement && (media.kind === 'image' || media.kind === 'other') ? (
        <img
          className="media__el"
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          width={media.width ?? undefined}
          height={media.height ?? undefined}
          onLoad={() => setState('loaded')}
          onError={() => setState('failed')}
        />
      ) : null}

      {state === 'loading' ? <span className="media__loading" aria-hidden="true" /> : null}
    </div>
  );
}

export function MediaBlock(props: MediaBlockProps) {
  return (
    <ErrorBoundary
      resetKey={props.media.assetId}
      fallback={(retry) => (
        <div className={cx('media', props.className)}>
          <div className="media__placeholder">
            <span className="media__kind">{MEDIA_KIND_LABEL[props.media.kind]}</span>
            <span className="media__hint">This media block failed to render.</span>
            <button type="button" className="table-action" onClick={retry}>
              Retry
            </button>
          </div>
        </div>
      )}
    >
      <MediaElement {...props} />
    </ErrorBoundary>
  );
}

export type MediaGalleryProps = {
  media: MediaRefDTO[];
  mediaEnabled: boolean;
  className?: string;
};

/** Post media area 鈥?one block per asset, laid out in a responsive grid. */
export function MediaGallery({ media, mediaEnabled, className }: MediaGalleryProps) {
  const blocks = media.filter((asset) => asset.role !== 'avatar' && asset.role !== 'banner');
  if (blocks.length === 0) return null;
  return (
    <div className={cx('media-grid', blocks.length > 1 && 'media-grid--multi', className)}>
      {blocks.map((asset) => (
        <MediaBlock key={asset.assetId} media={asset} mediaEnabled={mediaEnabled} ratio="16 / 10" />
      ))}
    </div>
  );
}

export type AvatarProps = {
  media: MediaRefDTO | null;
  name: string | null;
  mediaEnabled: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** A company page wears a rounded square instead of a circle — the one company-only visual. */
  kind?: ProfileKind;
  className?: string;
};

/**
 * Author avatar. With media loading off it renders initials rather than a "Load media" stub 鈥? * the per-block load action belongs on the post's media area, not on a 32 px circle (PRD).
 */
export function Avatar({
  media,
  name,
  mediaEnabled,
  size = 'md',
  kind = 'person',
  className,
}: AvatarProps) {
  const initials = (name ?? '?')
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  // The shape rides on both branches: the initials fallback is the avatar when media is off.
  const shape = cx('avatar', `avatar--${size}`, kind === 'company' && 'avatar--company', className);

  if (media === null || !mediaEnabled) {
    return (
      <span className={shape} title={name ?? undefined}>
        {initials || '?'}
      </span>
    );
  }

  return <MediaBlock media={media} mediaEnabled={mediaEnabled} alt={name ?? ''} className={shape} />;
}

export default MediaBlock;
